import { CSSProperties } from 'react';
import _ from 'lodash';
import { Property } from 'csstype';
import { TimeFormat, DateFormat } from '@Utils/Formats';
import { Currency, SelectAllMode } from '@Utils/Formats.UI';
import { ExcelOptions } from '../Charts/TimeSeriesStructs';
import { EnumType } from '@Primitive/FieldEditors';
import { ToolbarIconInfo } from '@Primitive/ToolbarIconButton';
import { CSVImportSettings } from '@UI/Tables/CSVImport';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import {
    ReactTableColumn, ReactTableStatics, ReactTableTextAlign,
    ColumnType, IconType,
    PivotSettings, PivotValueSelectionType,
    AddDuplicateRowPolicy,
    LocalizationLabelKey as RtLocLabelKey
} from '../../../Tables/ReactTable';
// ----------------------------------------------------------------------------------------------------
export const AutoHiddableWidth = 500;
export const __COLUMNS_VISIBILITY_KEY__ = '__COLUMNS_VISIBILITY__';
export const editRowTooltip = 'Edit row';
export enum LocalizationLabelKey {
    column = 'column_',
    tooltip = 'tooltip_',
    editRowButton = 'button_edit_',
    enumValue = 'enum_'
}
// ------------------------------------------------------------------------------------------------------
export interface TableSettings {
    Key: string;
    Columns: ColumnSettings[];
    DynamicColumnsWidth: boolean;
    PageListSettings: { PageList: number[], DefaultSelected: number };
    PriceCurrency: Currency;
    DateFormat: DateFormat;
    Editable: boolean;
    Selectable: boolean;
    SelectAllTooltip?: string;
    SelectAllMode?: number;
    SelectableTypeRadio: boolean;
    AutoSend: boolean;
    filterable: boolean;
    timeFormat: TimeFormat;
    WrapEnableByDefault: boolean;
    FilterEnableByDefault: boolean;
    ShowSubComponent: boolean;
    OnlyHiddenColumnsInSubcomponent: boolean;
    ShowEditRow: boolean;
    OnSaveSendEverything: boolean;
    PivotSupport: boolean;
    PivotSettings: PivotSettings;
    EnumSettings: IEnumSettings[];
    DropdownMenuSettings: any[];
    DownloadExcel: boolean;
    DownloadExcelOptions: ExcelOptions;
    CSVImport: boolean;
    CSVImportSettings: CSVImportSettings;
    DefaultImageURL: string;
    ImgWidth: number;
    ImgHeight: number;
    ObjectFit: Property.ObjectFit;
    ShowWrapText: boolean;
    ShowColVis: boolean;
    PagingSupport: boolean;
    ShowAddRow: boolean;
    AddDuplicateRowPolicy: AddDuplicateRowPolicy;
    ToolbarIcons: ToolbarIconInfo[];
    ToolbarHeight: number;
    ToolbarIconSize: number;
    SendSelectedButton: ToolbarIconInfo;
    Theme: {
        toolbarIconColor?: string;
        rowFontColor?: string;
        rowLineBottomColor?: string;
        rowLineBottomStyle?: string;
        rowLineBottomWidth?: number;
        rowSelectedBackgroundColor?: string;
        rowEditedBackgroundColor?: string;
        rowAddedBackgroundColor?: string;
        textAlign?: ReactTableTextAlign;
        headFontColor?: string;
        headLineBottomColor?: string;
        headLineBottomStyle?: string;
        headLineBottomWidth?: number;
        selectFontColor?: string;
        selectBackgroundColor?: string;
        selectBackgroundColorFocused?: string;
        selectBackgroundColorSelected?: string;
    },
    ServerSideProcessing: boolean;
}
// ------------------------------------------------------------------------------------------------------
export interface IEnumSettings {
    key: string;
    data: EnumType[];
    multi: boolean;
    creatable: boolean;
    useDialog: boolean;
    viewType?: EnumViewType;
}
// ------------------------------------------------------------------------------------------------------
export enum EnumViewType {
    Basic = 'basic',
    Label = 'label',
    Indicator = 'indicator'
}
// ------------------------------------------------------------------------------------------------------
export interface IDropdownMenuSettings {
    key: string;
    column: ColumnSettings;
    dropdownMenuItems: ToolbarIconInfo[];
}
// ------------------------------------------------------------------------------------------------------
export const getDefaultPageListSettings = (): { PageList: number[], DefaultSelected: number } => {
    return { PageList: [25, 50, 100, 200], DefaultSelected: 50 };
};
// ------------------------------------------------------------------------------------------------------
export interface ColumnSettings {
    Key: string;
    JsonName: string;
    Title: string;
    Type: ColumnType;
    IsPrimaryKey: boolean;
    Sortable: boolean;
    Filtering: boolean;
    Hidden: boolean;
    AutoHiddable: boolean;
    Tooltip: string;
    Icon: IconType;
    Width: number;
    PivotBy: boolean;
    PivotSettings?: PivotSettings;

    // For boolean type
    CheckedIcon: string;
    UncheckedIcon: string;

    // Highlight
    BackgroundColorAccessor: string;
    FontColorAccessor?: string;
    StyleAccessor: string;
    HeaderBackgroundColor?: string;

    // Edit
    NonEditable: boolean;
    VisibleInAdd: boolean;

    /**
     * Used only for custom columns creation.
     */
    EnumSettings?: IEnumSettings;
    DropdownSettings?: IDropdownMenuSettings;

    /**
     * Used for defining whether a column is mandatory
     */
    IsMandatory?: boolean;

    progressIsCircle?: boolean;
    progressColor?: string;
    progressWidth?: number;
    progressShowValue?: boolean;
    progressTrailColor?: string;
    progressTrailWidth?: number;
}
// ------------------------------------------------------------------------------------------------------
export const TableCellCommonStyle: CSSProperties = {
    textAlign: 'center',
    paddingLeft: 8,
    paddingRight: 8
}
// ------------------------------------------------------------------------------------------------------
export const enum TableKeys {
    EditDataPortKey = 'Edit_Data',
    SelectDataPortKey = 'Select_Data',
    InsertOrUpdatePortKey = 'Insert_or_Update',
    Settings = 'Settings',
    CustomColumns = 'Custom_Columns',
    DataPortKey = 'data_port',
    AddDataPortKey = 'Add_Data',
    onCellClick = 'onCellClick',
    onToolbarIconClick = 'onToolbarIconClick',
    onRowUpdate = 'onRowUpdate',
    onPageChange = 'onPageChange',
    onDropdownMenuItemClick = 'onDropdownMenuItemClick',
    ServerData = 'server_data',
    FetchData = 'fetch_data',
    onVisibilityChange = 'onVisibilityChange'
}
// ------------------------------------------------------------------------------------------------------
export enum onCellClickSrcKeys {
    Cell = 'cell',
    SubComponent = 'subComponent'
}
// ------------------------------------------------------------------------------------------------------
export const DefaultTableSettings: TableSettings = {
    Key: TableKeys.DataPortKey,
    Columns: [],
    DynamicColumnsWidth: true,
    PriceCurrency: Currency.USD,
    DateFormat: DateFormat.Short,
    PageListSettings: getDefaultPageListSettings(),
    Editable: false,
    Selectable: false,
    SelectAllTooltip: null,
    SelectAllMode: SelectAllMode.All,
    SelectableTypeRadio: false,
    AutoSend: false,
    filterable: true,
    timeFormat: TimeFormat.short,
    WrapEnableByDefault: true,
    FilterEnableByDefault: false,
    ShowSubComponent: false,
    OnlyHiddenColumnsInSubcomponent: false,
    ShowEditRow: false,
    OnSaveSendEverything: false,
    PivotSupport: false,
    PivotSettings: { Type: PivotValueSelectionType.Avg },
    EnumSettings: [],
    DropdownMenuSettings: [],
    DownloadExcel: false,
    DownloadExcelOptions: { fileName: "Data" },
    CSVImport: false,
    CSVImportSettings: {
        FirstRowIsHeaders: false,
        TrimColumnData: false,
        DynamicTyping: true
    },
    DefaultImageURL: "",
    ImgWidth: 24,
    ImgHeight: 24,
    ObjectFit: "cover",
    ShowWrapText: true,
    ShowColVis: true,
    PagingSupport: true,
    ShowAddRow: false,
    AddDuplicateRowPolicy: AddDuplicateRowPolicy.Allow,
    ToolbarIcons: [],
    ToolbarHeight: 25,
    ToolbarIconSize: 18,
    SendSelectedButton: {
        icon: "fas fa-paper-plane",
        tooltip: ReactTableStatics.GetLocalizationDictionary()[RtLocLabelKey.sendSelected]
    },
    Theme: {
        headFontColor: "#575757",
        headLineBottomColor: "rgb(87, 87, 87)",
        headLineBottomStyle: "hidden",
        headLineBottomWidth: 1,
        rowAddedBackgroundColor: "#0080005e",
        rowEditedBackgroundColor: "#ff000038",
        rowSelectedBackgroundColor: "lightgreen",
        rowFontColor: "#575757",
        rowLineBottomColor: "rgb(87, 87, 87)",
        rowLineBottomStyle: "hidden",
        rowLineBottomWidth: 1,
        toolbarIconColor: "#575757",
        selectFontColor: "#575757",
        selectBackgroundColor: "#ffffff",
        selectBackgroundColorFocused: "#deebff",
        selectBackgroundColorSelected: "#2684ff",
        textAlign: ReactTableTextAlign.Center
    },
    ServerSideProcessing: false
};
// ------------------------------------------------------------------------------------------------------
export const DefaultColumnSettings: ColumnSettings = {
    Key: "",
    JsonName: "",
    Title: "",
    Filtering: true,
    IsPrimaryKey: false,
    Sortable: true,
    Type: ColumnType.text,
    Icon: IconType.info,
    AutoHiddable: false,
    Tooltip: "",
    Width: 10,
    Hidden: false,
    NonEditable: false,
    IsMandatory: false,
    PivotBy: false,
    CheckedIcon: "fa-check-square-o",
    UncheckedIcon: "fa-square-o",
    BackgroundColorAccessor: null,
    FontColorAccessor: null,
    StyleAccessor: null,
    VisibleInAdd: true,
    progressIsCircle: false,
    progressColor: "#3498ff",
    progressWidth: 6,
    progressShowValue: true,
    progressTrailColor: "#e5e5ea",
    progressTrailWidth: 6
};
// ------------------------------------------------------------------------------------------------------
export interface TableData extends TableSettings {
    // old settings that we are going to delete in the future ...
    TableSettings?: TableSettings;
}
// ------------------------------------------------------------------------------------------------------
export interface TableState {
    columns: ReactTableColumn[];
    enumSettings: IEnumSettings[];
    dropdownMenuSettings: IDropdownMenuSettings[];

    // Overriden widget configuration.
    settings: Partial<TableSettings>;

    wrapTextEnabled: boolean;
    filterEnabled: boolean;

    // for server-side processing
    loading: boolean;
}
// ------------------------------------------------------------------------------------------------------
export interface StorageData {
    wrapTextEnabled: boolean;
    filterEnabled: boolean;
}
// ------------------------------------------------------------------------------------------------------
export interface IOnRowUpdate {
    index: number;
    values: any;
}
// ------------------------------------------------------------------------------------------------------
export interface IServerData {
    rows: any[];
    pages: number;
    reset: boolean;
}
// ------------------------------------------------------------------------------------------------------
export const EnumTypesJsonSchemeValidator = () => JsonSchemaValidator.GetInstance("EnumTypesJsonSchemeValidator", {
    title: "EnumTypes",
    type: "array",
    items: {
        type: "object",
        properties: {
            label: { type: "string" },
            value: { type: ["string", "number"] },
        },
        required: ["label", "value"]
    },
    minItems: 0
});
// ----------------------------------------------------------------------------------------------------
export const tableDataJsonSchemaValidator = () => JsonSchemaValidator.GetInstance("TableSchemeValidator", {
    type: "array",
    items: {
        type: "object"
    },
    minItems: 0
});
// ----------------------------------------------------------------------------------------------------
export const customColumnsValidator = () => JsonSchemaValidator.GetInstance('CustomColumnsPortValidator', {
    type: 'array',
    items: {
        type: 'object',
        required: ['Key', 'JsonName'],
        properties: {
            Key: { type: 'string' },
            Title: { type: 'string' },
            Width: { type: 'number' },
            Hidden: { type: 'boolean' },
            Tooltip: { type: 'string' },
            JsonName: { type: 'string' },
            PivotBy: { type: 'boolean' },
            Sortable: { type: 'boolean' },
            Filtering: { type: 'boolean' },
            CheckedIcon: { type: 'string' },
            NonEditable: { type: 'boolean' },
            AutoHiddable: { type: 'boolean' },
            UncheckedIcon: { type: 'string' },
            IsPrimaryKey: { type: 'boolean' },
            BackgroundColorAccessor: { type: 'string' },
            FontColorAccessor: { type: 'string' },
            StyleAccessor: { type: 'string' },
            VisibleInAdd: { type: 'boolean' },
            Icon: {
                type: 'number',
                enum: [0, 1, 2, 3, 4]
            },
            Type: {
                type: 'number',
                enum: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 13, 14]
            },
            EnumSettings: {
                type: 'object',
                required: ['data'],
                properties: {
                    multi: { type: 'boolean' },
                    data: {
                        type: 'array',
                        items: {
                            type: 'object',
                            required: ['label', 'value'],
                            properties: {
                                label: { type: 'string' },
                                value: { type: ['string', 'number'] }
                            }
                        }
                    }
                }
            }
        }
    }
});
// ----------------------------------------------------------------------------------------------------
export const settingsValidator = () => JsonSchemaValidator.GetInstance('SettingsPortValidator', {
    type: 'object',
    properties: {
        Editable: { type: 'boolean' },
        ShowAddRow: { type: 'boolean' }
    }
});
// ----------------------------------------------------------------------------------------------------
export const serverDataValidator = () => JsonSchemaValidator.GetInstance('ServerDataPortValidator', {
    type: 'object',
    properties: {
        rows: {
            type: 'array',
            items: { type: 'object' },
            minItems: 0
        },
        pages: { type: 'number' }
    }
});
// ----------------------------------------------------------------------------------------------------